import { Box, Heading, Flex, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import type { SocketMessage } from 'lib/socket/types';
import type { BlocksResponse } from 'types/api/block';

import { route } from 'nextjs-routes';

import useApiQuery, { getResourceKey } from 'lib/api/useApiQuery';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import { BLOCK } from 'stubs/block';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LatestBlocksItem from './LatestBlocksItem';

const LatestBlocks = () => {

  const borderColor = useColorModeValue('#e9ecef', 'gray.600');
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const hoverBgColor = useColorModeValue('#e9ecef', 'gray.700');
  // Always show 6 blocks like Etherscan
  const blocksMaxCount = 6;
  // Use the blocks API which returns more items
  const { data, isPlaceholderData, isError } = useApiQuery('blocks', {
    queryOptions: {
      placeholderData: { items: Array(blocksMaxCount).fill(BLOCK), next_page_params: null },
      // Fallback polling so latest block keeps updating even if websocket drops.
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
    },
  });

  const queryClient = useQueryClient();

  const handleNewBlockMessage: SocketMessage.NewBlock['handler'] = React.useCallback((payload) => {
    queryClient.setQueryData(getResourceKey('blocks'), (prevData: BlocksResponse | undefined) => {
      if (!prevData) return prevData;
      const newItems = [ ...prevData.items ];

      if (newItems.some((block => block.height === payload.block.height))) {
        return prevData;
      }

      const updatedItems = [ payload.block, ...newItems ].sort((b1, b2) => b2.height - b1.height).slice(0, blocksMaxCount);
      return { ...prevData, items: updatedItems };
    });
  }, [ queryClient, blocksMaxCount ]);

  const channel = useSocketChannel({
    topic: 'blocks:new_block',
    isDisabled: isPlaceholderData || isError,
  });
  useSocketMessage({
    channel,
    event: 'new_block',
    handler: handleNewBlockMessage,
  });

  let content;

  if (isError) {
    content = <Text>No data. Please reload the page.</Text>;
  }

  if (data?.items) {
    const dataToShow = data.items.slice(0, blocksMaxCount);

    content = (
      <>
        <VStack spacing={ 0 } overflow="hidden" alignItems="stretch">
          <AnimatePresence initial={ false } >
            { dataToShow.map(((block, index) => (
              <LatestBlocksItem
                key={ block.height + (isPlaceholderData ? String(index) : '') }
                block={ block }
                isLoading={ isPlaceholderData }
              />
            ))) }
          </AnimatePresence>
        </VStack>
        { /* View All Button - Etherscan exact style */ }
        <Flex
          as={ LinkInternal }
          href={ route({ pathname: '/blocks' }) }
          className="LatestTxnViewAll"
          justifyContent="center"
          alignItems="center"
          gap={ 2 }
          py={ 4 }
          bg={bgColor}
          borderTop="1px solid"
          borderColor="card_border"
          _hover={{ bg: hoverBgColor }}
          transition="background 0.2s"
          fontSize="13px"
          fontWeight={ 500 }
          textTransform="uppercase"
          letterSpacing="0.5px"
          color="text_primary"
          textDecoration="none"
        >
          VIEW ALL BLOCKS
          <Box as="span" ml={ 1 }>→</Box>
        </Flex>
      </>
    );
  }

  return (
    <Box
      width="100%"
      bg="card_bg"
      borderRadius="lg"
      border="1px solid"
      borderColor="card_border"
      boxShadow="card"
      overflow="hidden"
    >
      { /* Card Header */ }
      <Flex
        justifyContent="space-between"
        alignItems="center"
        py={ 2 }
        px={ 5 }
        borderBottom="1px solid "
        borderColor="card_border"
      >
        <Heading as="h4" fontSize="16px" color="text_primary" fontWeight={ 500 }>Latest Blocks</Heading>
      </Flex>
      { /* Card Body */ }
      <Box>
        { content }
      </Box>
    </Box>
  );
};

export default LatestBlocks;
